import React from 'react';
import HelpCenterSharpIcon from '@mui/icons-material/HelpCenterSharp';
import { Button, Modal, Box, Typography } from '@mui/material';
import { MathJax } from 'better-react-mathjax';


const DemoModal = (props) => {
  const [open, setOpen] = React.useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const eqh = `
    \\[
    \\operatorname{sign}{\\left(\\mathbf{Wx}\\right)},\\quad \\mathbf{W} \\in \\mathbb{R}^{n \\times d},\\quad n=\\text{# labels},\\quad d=\\text{# features}
    \\]
  `;

  const x = `\\( \\mathbf{x} \\)`;
  const W = `\\( \\mathbf{W} \\)`;
  const C = `\\( \\mathbf{C} = \\{1, -1\\}^n \\)`;
  const R2 = `\\( \\mathbb{R}^2 \\)`;

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 800,
    maxHeight: '80%',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    overflowY: 'auto',  // Enable vertical scrolling
    p: 4,
  };

  return (
    <div className="modal-div">
      <Button color="primary" onClick={handleOpen}>
        <HelpCenterSharpIcon className="modal-button"/>
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={style}>
          <MathJax>
            <Typography id="modal-title" variant="h6" component="h2">
              <h2> Hyperplane Arrangement/Zonotope Duality:</h2>
              <h4 id="footnote">An Interactive Demo by <a href="https://grv.unargmaxable.ai" target="_blank">Andreas Grivas</a> (best viewed on desktop).</h4>
            </Typography>
            <Typography id="modal-description" sx={{ mt: 2 }}>
              <h2>Motivation </h2>
              In Machine Learning, a linear classifier parametrised by {W} that has more outputs than inputs has outputs that can never be predicted.
              We call such outputs <a href="https://unargmaxable.ai" target="_blank">unargmaxable</a>.
              For this demo, consider a multilabel classifier parametrised by {W} ({props.N} output labels and 2 inputs). It makes predictions for an input {x} by computing sign vectors: a (+) means the label is assigned and (-) means it is not assigned. Namely, the prediction for {x} is:
              {eqh}
              This demo shows the argmaxable sign vectors for the given {W}. The sign vectors that fade out on the Zonotope View are unargmaxable. See <a href="https://arxiv.org/abs/2310.10443" target="_blank">our paper</a> for more details.
              <h2> Goal </h2>
              <p> More abstractly, this demo shows the correspondence between regions in the <a href="https://en.wikipedia.org/wiki/Arrangement_of_hyperplanes" target="_blank"><span className="left">Hyperplane Arrangement</span></a> and the vertices of the <a href="https://en.wikipedia.org/wiki/Hypercube" target="_blank"><span className="right">{props.N}-cube</span></a> that survive on the <a href="https://en.wikipedia.org/wiki/Convex_hull" target="_blank"><span className="right">Convex Hull</span></a> of its linear projection (a <a href="https://en.wikipedia.org/wiki/Zonohedron" target="_blank"><span className="right">Zonotope</span></a>). Both the hyperplanes and the projection are defined by the same matrix, {W} (see bottom left).
              </p>
              <h2> Interact: How to change {W} </h2>
              <p>You can click and drag the arrows on the left to change {W}, i.e. moving an arrow changes the corresponding row of parameters in the matrix at the bottom left. You can also increase the number of hyperplanes by dragging the slider at the bottom.</p>
              <h2>Interpretation (more details) </h2>
              <h3 className="left"> Hyperplane View (left) </h3>
              <p>Each row of {W} defines a hyperplane (i.e. each vector is perpendicular to a line in 2d).
              The hyperplanes discretise {R2} into regions. We label each region with {props.N} signs, a <span className="signc">+</span> if the region is above the hyperplane in the direction of the arrow and a <span className="signc">-</span> if it is below.</p>
              <h3 className="right"> Zonotope View (right) </h3>
              <p>We visualise the projection of the vertices of the n-cube {C} as projected by {W} from n={props.N} dimensions to 2 dimensions. We draw the vertices that fall on the convex hull of the projection in black and fade out the vertices that fall in its relative interior.</p>
              <h3> Takeaway </h3>
              <p><b>A vertex of the cube survives on the convex hull of the projection by {W} if there is a corresponding region in the hyperplane arrangement defined by {W} (and vice-versa).</b></p>
              <h2> Credits </h2>
              <p>This demo uses <a href="https://github.com/lovasoa/highs-js">HiGHS js</a> to solve Linear Programmes that determine the feasible regions in the hyperplane arrangement and the vertices on the convex hull.</p>
            </Typography>
          </MathJax>
        </Box>
      </Modal>
    </div>
  );
};

export default DemoModal;
